import { BlockError } from '@/blocks.04-project/block-error/block-error';
import counter from '@/blocks.04-project/counter/counter.tmpl-specs/base.module';
import giftPopper from '@/blocks.04-project/gift-popper/gift-popper.tmpl-specs/base.module';
import get from 'lodash-es/get';
import find from 'lodash-es/find';
import includes from 'lodash-es/includes';
import isEmpty from 'lodash-es/isEmpty';
import forEach from 'lodash-es/forEach';
import toArray from 'lodash-es/toArray';
import map from 'lodash-es/map';
import isNull from 'lodash-es/isNull.js';
import compact from 'lodash-es/compact';
import join from 'lodash-es/join';
import debounce from 'lodash-es/debounce';
import toNumber from 'lodash-es/toNumber';
import filter from 'lodash-es/filter';
import isArray from 'lodash-es/isArray';
import reduce from 'lodash-es/reduce';
import merge from 'lodash-es/merge';
import floor from 'lodash-es/floor';
import set from 'lodash-es/set';

/**
 * Класс для работы с товарами
 * @example
 *
 * Аттрибуты:
 * [data-offer-id] - {string} - id текущего офера у товара
 *
 * Аттрибуты на элементах:
 * [data-search-card] - {boolean} - карточка товара в поиске
 * [data-control-counter] - {boolean} - элемент каунтера
 * [data-sku] - {boolean} - элемент выбора цвета
 * [data-control-cart] - {boolean} - элемент добавления в корзину
 * [data-control-favorite] - {boolean} - элемент добавления в избранное
 * [data-control-compare] - {boolean} - элемент добавления в сравнение
 * [data-images] - {boolean} - элемент картинок
 * [data-badges-base] - {boolean} - элемент обычных тегов
 * [data-badges-promo] - {boolean} - элемент тегов с иконками
 * [data-title] - {boolean} - элемент названия товара
 * [data-article] - {boolean} - элемент id товара
 * [data-url] - {boolean} - элемент ссылки на товар
 * [data-price] - {boolean} - элемент цены
 * [data-status] - {boolean} - контейнер для текста статуса
 * [data-out-hide] - {boolean} - скрыт если товар отсутствует
 * [data-out-visible] - {boolean} - отображён если товар отсутствует
 * [data-actions] - {boolean} - элемент сравнение/избранное
 * [data-specs-preview-container] - {boolean} - контейнер для спецификаций с описанием
 * [data-specs-preview] - {boolean} - элемент малых спецификаций с описанием
 *
 */
class ProductControl {
  /***
   * Конструктор класса
   * @param blockClass {String} класс блока, от которого наследуется
   * @param selector {String|undefined} селектор для поиска блока
   * @param options {Object} данные карточки
   * @param elem {HTMLElement} элемент для инициализации
   */
  constructor({ blockClass, selector, options, elem }) {
    this.BlockError = new BlockError({ blockName: 'ProductControl', destroy: this.destroy });

		this.blockClass = blockClass;
		this.controlSelector = '.product-control, .gift-set-control';

		this.selector = selector;
		this.block = elem || document.querySelector(this.selector);
    if (!this.block) return this.BlockError.error(`Отсутствует ${this.selector} или ${elem}`);

    this.block.ProductControl = this;
		this.getData(options);

		window.getBH((bh) => { this.bh = bh; });
  }

  init() {
		this.setOffer(get(this.currentOffer, 'id'));
		this.block.setAttribute('data-offer-id', get(this.currentOffer, 'id', ''));

		this.block.addEventListener('change', this.changeHandler.bind(this));
		this.block.addEventListener('click', this.clickHandler.bind(this));

		this.block.classList.add('product-control_inited');
  }

	/***
	 * Обработка всех событий change на блоке
	 * @param event - событие
	 */
	changeHandler(event) {
		const closestControl = event.target.closest(this.controlSelector);
		if (closestControl !== this.block) return;

		if (event.target.closest('[data-sku] input[type="radio"]')) {
			this.skuChange();
			return;
		}

		if (event.target.closest('[data-control-counter]') && event.target.tagName === 'INPUT') {
			const oldQuantity = toNumber(get(find(window.Block.Basket.products, { offerId: this.currentOffer.id }), 'quantity', 0));

			event.preventDefault();
			const quantity = toNumber(event.target.value);
			quantity < 1 && this.toggleCounter({ toggle: false });
			forEach(this.getOffersCard(), (card) => card?.ProductControl?.toggleCounter({
				toggle: quantity >= 1, quantity: quantity,
			}));
			this.updateProducts(quantity, oldQuantity);
		}
	}

	/***
	 * Обработка всех событий change на блоке
	 * @param event - событие
	 */
	clickHandler(event) {
		const closestControl = event.target.closest(this.controlSelector);
		if (closestControl !== this.block) return;

		if (event.target.closest('[data-control-cart]')) {
			const oldQuantity = toNumber(get(find(window.Block.Basket.products, { offerId: this.currentOffer.id }), 'quantity', 0));
			if (oldQuantity !== 0) return;

			event.preventDefault();
			this.toggleCounter({ toggle: true });
			forEach(this.getOffersCard(), (card) => card?.ProductControl?.toggleCounter({
				toggle: true, quantity: 1,
			}));
			this.updateProducts(1, oldQuantity);
			return;
		}

		if (event.target.matches('[data-control-favorite]')) {
			this.updateFavorite();
			return;
		}

		if (event.target.matches('[data-control-compare]')) {
			this.updateCompare();
			return;
		}

    if (event.target.matches('[data-control-request-price]')) {
      this.showRequestPriceForm();
      return;
    }
	}

	/**
	 * Изменение оффера после изменения sku
	 */
	skuChange() {
		const inputs = filter(this.block.querySelectorAll('[data-sku] input[type="radio"]:checked'), (item) => {
			return item.closest(this.controlSelector) === this.block;
		});
		const data = reduce(inputs, (acc, { value }) => merge(acc, { 'color': value }), {});
		const id = get(find(this.offers, data), 'id');
		this.setOffer(id);
	}

	render() {
		if (this.offerHash !== this.getCurrentHash()) {
			this.renderImages();
		}
		this.renderBadges();
		this.renderTitle();
		this.renderArticle();
		this.renderRating();
		this.renderReviews();
		this.renderSpecsPreview();
		this.renderShortSpecs();
		this.renderSpecs();
		this.renderPrice();
		this.renderControl();
		this.renderStatus();
		this.toggleAvailability();
	}

	renderImages() {
		const containers = this.getBlockElements('[data-images]');
		const images = get(this.currentOffer, 'photos');
		forEach(containers, (container) => {
			container.innerHTML = '';
			if (images.length > 1) {
				container.insertAdjacentHTML('beforeend', this.bh.apply([
					{cls: 'swiper', content: [
							{cls: 'swiper-wrapper', content: map(images, ({ src, alt }) => [
									{cls: 'swiper-slide', content: [
											{block: this.blockClass, elem: 'img', content: [
													{block: 'image', mods: {dynamic: true}, cls: 'text-center align-middle', content: [
															{block: 'img', attrs: {loading: 'lazy'}, src: src || './images/default-image/default-image.svg', alt, title: alt},
														]},
												]},
										]},
								])},
						]},
				]));
			} else {
				container.insertAdjacentHTML('beforeend', this.bh.apply([
					!isEmpty(images) && map(images, ({ src, alt }) => [
						{block: this.blockClass, elem: 'img', content: [
								{block: 'image', mods: {dynamic: true}, cls: 'text-center align-middle', content: [
										{block: 'img', attrs: {loading: 'lazy'}, src: src || './images/default-image/default-image.svg', alt},
									]},
							]},
					]),
					isEmpty(images) && {block: this.blockClass, elem: 'img', content: [
							{block: 'image', mods: {dynamic: true}, cls: 'text-center align-middle', content: [
									{block: 'img', attrs: {loading: 'lazy'}, src: './images/default-image/default-image.svg', alt: ''},
								]},
						]},
				]));
			}
		});
	}

	renderBadges() {
		const allBadges = get(this.currentOffer, 'badges', []);
		const gifts = get(this.currentOffer, 'gifts', []);

		const rightBadges = filter(allBadges, ({ code }) => includes(['free_shipping', 'black_friday', 'cyber_monday', 'cyber_summer', 'low_price', 'gift', 'sale_11_11'], code));
		const leftBadges = filter(allBadges, ({ code }) => includes(['new', 'hot', 'discount_percent', 'lifetime'], code));
    const badgesTechnology = filter(allBadges, ({ code }) => includes(['duhrer', 'schmieroll'], code));
		const containersLeft = this.getBlockElements('[data-badges-base]');
		const containersRight = this.getBlockElements('[data-badges-promo]');
    const containersTechnology = this.getBlockElements('[data-badges-technology]');

    const imageUrl = NOT_PROD ? './images/badges' : '/local/templates/ermenrich/dist/images/badges';

		const badgesStyles = {
			'hot': 'badge badge-promotion badge-red',
			'lifetime': 'badge-promotion badge-orange-gradient',
			'new': 'badge-promotion badge-green',
			'discount_percent': 'badge badge-promotion badge-black badge-wrap',
			'cyber_monday': 'badge-event badge-cyber-monday badge-blue-gradient text-uppercase',
      'cyber_summer': 'badge-event badge-cyber-summer text-uppercase',
			'free_shipping': 'badge-event badge-free-delivery text-uppercase mb-auto',
			'black_friday': 'badge badge-event badge-black-friday text-uppercase me-12',
			'low_price': 'badge-event badge-price-guarantee badge-indigo text-uppercase me-12',
			'gift': 'badge-event badge-gift badge-indigo text-uppercase',
			'sale_11_11': 'badge-event badge-sale-eleven-eleven text-uppercase',
      'duhrer': 'badge-technology',
      'schmieroll': 'badge-technology',
		};

		forEach(containersLeft, (container) => {
			container.innerHTML = '';
			if (isEmpty(leftBadges)) return;

			container.insertAdjacentHTML('beforeend', this.bh.apply(map(leftBadges, ({ code, text }) => [
				code === 'discount_percent' ? [
					{cls: get(badgesStyles, code), content: [
							{content: text},
							{content: window.BX?.message('cardBadgeDiscount') || 'скидка'},
						]},
				] : [
					{block: 'badge', cls: get(badgesStyles, code), content: text},
				]
			])));
		});

		forEach(containersRight, (container) => {
			container.innerHTML = '';
			if (isEmpty(rightBadges)) return;
			container.insertAdjacentHTML('beforeend', this.bh.apply(map(rightBadges, ({ code, text, src, title }) => [
				code === 'gift' ? [
					{block: 'badge', tag: 'button', cls: get(badgesStyles, code), attrs: {
							'data-bs-toggle': 'tooltip',
							'data-bs-placement': 'bottom',
							'data-bs-fallback-placements': '[]',
							'data-bs-offset': '0, 8',
							'data-bs-custom-class': 'tooltip-gift',
							'data-bs-trigger': 'focus',
							'data-closest': true,
							'data-bs-container': '.product-control',
							'data-bs-html': 'true',
							'data-bs-title': `${this.bh.apply([giftPopper({ gifts, title: window.BX?.message('giftTooltipTitle') || 'Подарок' })])}`,
						}, content: text}
				] : [
					{block: 'badge', cls: get(badgesStyles, code), content: [
							{content: text},
							{block: 'img', mods: {lazy: true, contain: true}, src, attrs: {alt: title, title: title}}
						]}
				]
			])));
			container.addEventListener('shown.bs.tooltip', (event) => event.target.closest('[data-badges-promo]') && window.Block.CustomScrollbar.init(this.block));
			window.Block.Tooltip.init(container);
		});

    forEach(containersTechnology, (container) => {
      container.innerHTML = '';
      if (isEmpty(badgesTechnology)) return;

      container.insertAdjacentHTML('beforeend', this.bh.apply(map(badgesTechnology, ({ code, link, alt, title }) => [
        {block: 'badge', cls: get(badgesStyles, code), content: [
            {block: 'img', mods: {lazy: true, contain: true}, src: `${imageUrl}/${code}.svg`, attrs: {alt: alt, title: title}},
            link && {tag: 'noindex', content: [
                {elem: 'link ', tag: 'a', attrs: {href: link, title: title}}
              ]},
          ]}
      ])));
    });
  }

	renderTitle() {
		const title = get(this.data, 'title', '');
		const containers = this.getBlockElements('[data-title]');
		this.updateUrl();

		forEach(containers, (container) => container.innerHTML = title);
	}

	renderArticle = () => {
		const article = get(this.currentOffer, 'article', null);
		const articleText = `${window.BX?.message('articlePrefix') || 'Арт.'} ${article}`;
		const containers = this.getBlockElements('[data-article]');

		forEach(containers, (container) => container.innerHTML = articleText || '');
	};

	updateUrl() {
		const url = get(this.currentOffer, 'url', null);
		const containers = this.getBlockElements('[data-url]');

		forEach(containers, (container) => container.href = url || '');
	}

	renderRating() {
		const rating = floor(get(this.data, 'rating', null), 1);
		const containers = this.getBlockElements('[data-rating]');

		forEach(containers, (container) =>  {
			container.innerHTML = '';
			if (!rating) return;

			container.insertAdjacentHTML('beforeend', this.bh.apply([
				{block: 'fi', mods: {icon: 'star'}},
				rating,
			]));
		});
	}

	/**
	 * Рендер отзывов
	 * @param langVar{String} - переменная перевода
	 * @param customText{String|null} - текст заглушка
	 * @param icon{Boolean} - нужна ли иконка
	 */
	renderReviews({ langVar = '', customText = null, icon = true } = {}) {
		const reviews = floor(get(this.data, 'reviews', null), 1);
		const containers = this.getBlockElements('[data-reviews]');

		forEach(containers, (container) =>  {
			container.innerHTML = '';
			if (!reviews) return;

			container.insertAdjacentHTML('beforeend', this.bh.apply([
        icon && {block: 'fi', mods: {icon: 'chat'}},
				customText ? [
					window.BX?.Loc?.getMessagePlural(langVar, reviews, {'#COUNT#': reviews}) || customText.replace('#COUNT#', reviews)
				] : reviews
			]));
		});
	}

	renderSpecsPreview() {
		const specsPreview = get(this.currentOffer, 'specsPreview', null);
		const containers = this.getBlockElements('[data-specs-preview]');

		forEach(containers, (container) =>  {
			container.innerHTML = '';
			if (!specsPreview) return;

			container.insertAdjacentHTML('beforeend', this.bh.apply(map(specsPreview, ({ title, description }) => [
				{block: this.blockClass, elem: 'spec-preview', cls: 'a a_color_secondary', tag: 'button', attrs: {
						'data-bs-toggle': 'tooltip',
						'data-bs-title': description,
						'data-bs-placement': 'top',
						'data-bs-fallback-placements': '[]',
						'data-bs-offset': '0, 8',
						'data-bs-trigger': 'hover',
						'data-bs-container': '[data-specs-preview-container]',
						'data-closest': true,
					}, content: title},
			])));
			window.Block.Tooltip.init(container);
		});
	}

	renderShortSpecs() {
    const shortSpecs = get(this.currentOffer, 'shortSpecifications', null);
    const containers = this.getBlockElements('[data-short-specifications]');

    forEach(containers, (container) =>  {
      container.innerHTML = '';
			if (!shortSpecs) return;

      container.insertAdjacentHTML('beforeend', this.bh.apply(map(shortSpecs, ({ name, values }) => [
        {block: this.blockClass, elem: 'spec', content: [
            {elem: 'spec-title', content: name},
            {elem: 'spec-value', content: map(values, ({ text, link, type }, idx) => [
                {tag: link ? 'a' : 'span', attrs: link && {href: link }, content: [
                    type === 'boolean' ? [
                      {tag: 'span', cls: 'visual-hidden', content: '✓'},
                      {block: 'fi', cls: 'text-success', mods: {icon: 'check'}},
                    ] : text,
                  ]},
                ++idx !== values.length && ', '
              ])},
          ]}
      ])));
    });
	}

	renderSpecs() {
    const specs = get(this.currentOffer, 'specifications', null);
    const containers = this.getBlockElements('[data-specifications]');

    forEach(containers, (container) =>  {
      container.innerHTML = '';
			if (!specs) return;

      container.insertAdjacentHTML('beforeend', this.bh.apply(map(specs, ({ name, values }) => [
        {block: this.blockClass, elem: 'spec', content: [
            {elem: 'spec-title', content: name},
            {elem: 'spec-value', content: map(values, ({ text, link, type }, idx) => [
                {tag: link ? 'a' : 'span', attrs: link && {href: link }, content: [
                    type === 'boolean' ? [
                      {tag: 'span', cls: 'visual-hidden', content: '✓'},
                      {block: 'fi', cls: 'text-success', mods: {icon: 'check'}},
                    ] : text,
                  ]},
                ++idx !== values.length && ', '
              ])},
          ]}
      ])));
    });
	}

	renderPrice() {
		const price = get(this.currentOffer, 'price', null);
		const oldPrice = get(this.currentOffer, 'oldPrice', null);
		const request = get(this.currentOffer, 'priceOnRequest', false);
		const containers = this.getBlockElements('[data-price]');

		forEach(containers, (container) => {
			container.innerHTML = '';
			if (this.canBuy && !isEmpty(price) && !request) {
				container.insertAdjacentHTML('beforeend', this.bh.apply([
					price,
					oldPrice && {tag: 'del', content: oldPrice}
				]));
			}
		});
	}

	renderStatus() {
		const statusText = get(this.currentOffer, 'status.text');
		const containers = this.getBlockElements('[data-status]');

		forEach(containers, (container) => {
			container.innerHTML = statusText || 'Нет в<br>наличии';
		});
	}

	renderControl() {
		this.renderBasket();
		this.renderActions();
	}

	renderBasket() {
		const id = get(this.currentOffer, 'id');
    const request = get(this.currentOffer, 'priceOnRequest', false);
		const substitutesUrl = get(this.currentOffer, 'substitutesUrl', '');
		const quantity = toNumber(get(this.currentOffer, 'quantity', null));
		const isExistBasket = !isNull(get(window, 'Block.Basket.products', null));
		const basket = toNumber(get(find(window.Block.Basket.products, { offerId: id }), 'quantity', 0));
		const isInBasket = basket > 0;

		const containers = this.getBlockElements('[data-basket]');
		if (!containers.length) return;

		forEach(containers, (container) => {
			container.innerHTML = '';
			container.insertAdjacentHTML('beforeend', this.bh.apply([
        !request && [
          {block: this.blockClass, elem: 'counter', content: [
              counter({
                attrs: {
                  'data-out-hide': true,
                  'data-coming-hide': true,
                  'data-control-counter': true,
                },
                value: basket,
                max: quantity,
                disabled: !this.canBuy || !quantity,
              }),
            ]},
          {block: 'btn', cls: 'btn-primary btn-lg', tag: 'button',
            attrs: {
              'data-out-hide': true,
              'data-coming-hide': true,
              'data-control-cart': true,
              disabled: !this.canBuy || !isExistBasket
            },
            content: window.BX?.message('addInBasket') || 'В корзину'},
          substitutesUrl && {block: 'btn', cls: 'btn-primary-light fw-medium', attrs: {'data-out-visible': true, href: substitutesUrl}, content: window.BX?.message('findSame') || 'Выбрать похожие'},
        ],
        request && [
          !this.isSearchCard && {block: 'btn', cls: 'btn-primary', tag: 'button', attrs: {'data-control-request-price': true}, content: window.BX?.message('priceOnRequest') || 'Цена по запросу'},
          this.isSearchCard && {block: 'h4', content: window.BX?.message('priceOnRequest') || 'Цена по запросу'},
        ],
      ]));
		});

		this.toggleCounter({ toggle: this.canBuy && isInBasket, quantity: basket });
	}

	renderActions() {
		const id = get(this.currentOffer, 'id');
		const compare = window.Block.Compare.includes({ offerId: id });
		const favorite = includes(window.Block.Favorite.favorite, id);
		const share = get(this.currentOffer, 'share', []);
		const isExistFavorite = !isNull(get(window, 'Block.Favorite.favorite', null));
		const isExistCompare = !isNull(get(window, 'Block.Compare.compare', null));
		const containers = this.getBlockElements('[data-actions]');

		if (!containers.length) return;

		const favoriteTitle = this.bh.apply([
			!favorite && (window.BX?.message('addFavorites') || 'Добавить в избранное'),
			favorite && (window.BX?.message('removeFavorites') || 'Удалить из избранного'),
		]);

		const compareTitle = this.bh.apply([
			!compare && (window.BX?.message('addCompare') || 'Добавить в сравнение'),
			compare && (window.BX?.message('removeCompare') || 'Удалить из сравнения'),
		]);

		const shareTitle = window.BX?.message('productDetailShareTitle') || 'Поделиться';

		forEach(containers, (container) => {
			container.innerHTML = '';
			container.insertAdjacentHTML('beforeend', this.bh.apply(map(this.actionList, (action) => [
				action === 'favorite' && [
					{block: this.blockClass, elem: 'action-btn', tag: 'button',
						cls: join(compact([favorite && 'active']), ' '),
						attrs: {
							type: 'button', 'data-control-favorite': true,
							'disabled': !isExistFavorite,
							'title': favoriteTitle,
						},
						content: [
							{block: 'fi', mods: {icon: 'favorite'}},
							{block: 'fi', mods: {icon: 'favorite-fill'}}
						]}
				],
				action === 'compare' && [
					{block: this.blockClass, elem: 'action-btn', tag: 'button',
						cls: join(compact([compare && 'active']), ' '),
						attrs: {
							type: 'button', 'data-control-compare': true,
							'disabled': !isExistCompare,
							'title': compareTitle,
						}, content: [
							{block: 'fi', mods: {icon: 'chart'}},
							{block: 'fi', mods: {icon: 'chart-fill'}},
						]},
				],
				action === 'share' && !isEmpty(share) && [
					{cls: 'dropdown', content: [
							{block: this.blockClass, elem: 'action-btn', tag: 'button',
								attrs: {
									'data-bs-toggle': 'dropdown',
									type: 'button', 'data-control-share': true,
									'title': shareTitle,
								}, content: [
									{block: 'fi', mods: {icon: 'share-link'}},
								]},
							{block: this.blockClass, elem: 'share-dropdown', cls: 'dropdown-menu', content: map(share, ({ url, icon, text, target }) => [
								target === 'copy' && [
									{elem: 'dropdown-item', cls: 'dropdown-item', tag: 'button',
										attrs: {
											'data-iv-copy': true,
											'data-copy-text': url,
											'data-bs-title': window.BX?.message('productDetailShareLinkCopy') || 'Ссылка&nbsp;скопирована',
											'data-bs-container': '.dropdown',
											'data-tooltip-reference': '.dropdown',
											'data-bs-placement': 'top',
											'data-bs-html': 'true',
											'data-closest': true,
										}, content: [
											{block: 'fi', mods: { icon }},
											text,
										]},
								],
								target !== 'copy' && [
									{elem: 'dropdown-item', cls: 'dropdown-item', tag: 'a', attrs: {href: url, target}, content: [
											{block: 'fi', mods: { icon: icon }},
											text,
										]},
								],
							])},
						]}
				],
			])));

			window.Block.Copy.init(container);
		});

		this.favoriteButtons = this.getBlockElements('[data-control-favorite]');
		this.compareButtons = this.getBlockElements('[data-control-compare]');
	}

	/***
	 * Обновление товара в корзине
	 * @param quantity {Number} - новое количество
	 * @param oldQuantity {Number} - старое количество
	 */
	updateProducts = debounce((quantity, oldQuantity) => {
		const offerId = get(this.currentOffer, 'id');
		const max = get(this.currentOffer, 'quantity', null);
		this.toggleDisabledCounter({ toggle: true });
		forEach(this.getOffersCard(), (card) => card?.ProductControl?.toggleDisabledCounter({ toggle: true }));

		window.Block.Basket.updateProducts({ offerId, quantity })
			.then((res) => {
				if (res) {
					const limit =  get(res, 'limit', false);
					const newQuantity = toNumber(get(res, 'quantity', 0));
					const toggle = limit || newQuantity === 0 && toNumber(quantity) !== 0;

					this.toggleCounter({ toggle: !toggle && quantity > 0, quantity: newQuantity, });
					this.toggleDisabledCounter({ toggle, limit: newQuantity >= max });

					forEach(this.getOffersCard(), (card) => card?.ProductControl?.toggleCounter({
						toggle: !toggle && quantity > 0, quantity: newQuantity,
					}));

					forEach(this.getOffersCard(), (card) => card?.ProductControl?.toggleDisabledCounter({
						toggle, limit: newQuantity >= max
					}));

					// Добавлено по просьбе программистов, им нужно отслеживать когда поменялось корзина
					this.block.dispatchEvent(new CustomEvent('updateBasket.iv.productControl', { bubbles: true, detail: {
							quantity,
							oldQuantity,
							currentOffer: this.currentOffer,
						}}));
				}
			});
	}, 400);

	/***
	 * Обновление избранного товара
	 * @param toggle {boolean|null} - принудительную переключение без запроса
	 */
	updateFavorite(toggle = null) {
		if (!isNull(toggle)) {
			this.toggleFavorite(toggle);
			return;
		}

		const offerId = get(this.currentOffer, 'id');
		const isInFavorite = includes(window.Block.Favorite.favorite, offerId);
		forEach(this.favoriteButtons, (el) => el.setAttribute('disabled', ''));

		window.Block.Favorite.toggle({ toggle: !isInFavorite, offerId })
			.then((res) => {
				if (res) this.toggleFavorite(!isInFavorite);
				forEach(this.favoriteButtons, (el) => el.removeAttribute('disabled'));
				forEach(this.getOffersCard(), (card) => card?.ProductControl.toggleFavorite(!isInFavorite));
			});
	}

	/***
	 * Обновление сравнения товара
	 * @param toggle {boolean|null} - принудительное переключение без запроса
	 */
	updateCompare(toggle = null) {
		if (!isNull(toggle)) {
			this.toggleCompare(toggle);
			return;
		}

		const offerId = get(this.currentOffer, 'id');
		const isInCompare = window.Block.Compare.includes({ offerId });
		forEach(this.compareButtons, (el) => el.setAttribute('disabled', ''));

		window.Block.Compare.toggle({ toggle: !isInCompare, offerId })
			.then((res) => {
				if (res) this.toggleCompare(!isInCompare);
				forEach(this.compareButtons, (el) => el.removeAttribute('disabled'));
				forEach(this.getOffersCard(), (card) => card?.ProductControl.toggleCompare(!isInCompare));
			});
	}

	/***
	 * Обновления title у кнопок избранное
	 * @param isFavorite {Boolean} - добавлен/удалён
	 * @param target {HTMLElement} - нажатая кнопка
	 */
	updateFavoriteTitle(isFavorite, target) {
		const favoriteTitle = this.bh.apply([
			!isFavorite && (window.BX?.message('addFavorites') || 'Добавить в избранное'),
			isFavorite && (window.BX?.message('removeFavorites') || 'Удалить из избранного'),
		]);

		target.setAttribute('title', favoriteTitle);
	}

	/***
	 * Обновления title у кнопок сравнения
	 * @param isCompare {Boolean} - добавлен/удалён
	 * @param target {HTMLElement} - нажатая кнопка
	 */
	updateCompareTitle(isCompare, target) {
		const compareTitle = this.bh.apply([
			!isCompare && (window.BX?.message('addCompare') || 'Добавить в сравнение'),
			isCompare && (window.BX?.message('removeCompare') || 'Удалить из сравнения'),
		]);

		target.setAttribute('title', compareTitle);
	}

	/***
	 * Переключение состояния кнопок "В избранное"
	 * @param toggle{boolean} - добавлено/удалено
	 */
	toggleFavorite(toggle) {
		forEach(this.favoriteButtons, (el) => {
			el.classList.toggle('active', toggle);
			this.updateFavoriteTitle(toggle, el);
			this.block.classList.toggle('product-control_in-favorite', toggle);
		});

		// Добавлено по просьбе программистов, им нужно отслеживать когда изменился список избранного
		if (toggle) {
			this.block.dispatchEvent(new Event('addFavorite.iv.productControl', {bubbles: true}));
		} else {
			this.block.dispatchEvent(new Event('removeFavorite.iv.productControl', {bubbles: true}));
		}
	}

	/***
	 * Переключение состояния кнопок "В сравнение"
	 * @param toggle{boolean} - добавлено/удалено
	 */
	toggleCompare(toggle) {
		const offerId = get(this.currentOffer, 'id');
		forEach(this.compareButtons, (el) => {
			el.classList.toggle('active', toggle);
			this.updateCompareTitle(toggle, el);
			this.block.classList.toggle('product-control_in-compare', toggle);
		});
		// Добавлено по просьбе программистов, им нужно отслеживать когда изменился список сравнения
		if (toggle) {
			this.block.dispatchEvent(new CustomEvent('addCompare.iv.productControl', {bubbles: true, detail: { offerId }}));
		} else {
			this.block.dispatchEvent(new CustomEvent('removeCompare.iv.productControl', {bubbles: true, detail: { offerId }}));
		}
	}

	/***
	 * Переключение состояния текста сообщения о недоступности товара
	 */
	toggleAvailability() {
		if (this.comingSoon) {
			this.block.classList.add('product-control_status_coming-soon');
			this.block.classList.remove('product-control_status_out-of-stock');
		} else {
			this.block.classList.remove('product-control_status_coming-soon');
			this.block.classList.toggle('product-control_status_out-of-stock', !this.canBuy);
		}
	}

  /***
   * Показ окна для запроса цены
   */
  showRequestPriceForm = () => {
    const priceRequestFormId = get(this.currentOffer, 'priceRequestFormId');
    const vendorCode = get(this.currentOffer, 'article', '');
    const component = get(window, 'IntervolgaCommon.AjaxComponent.List')?.get(priceRequestFormId);
    if (!component) return;

    component.__componentParams['PRODUCT_ARTICLE_FIELD'] = vendorCode;
    component.call();
  };

	/***
	 * Переключение каунтера на кнопку и обратно. Обновление значения каунтера
	 * @param toggle {boolean} - добавлен/удалён
	 * @param quantity {Number} - количество в каунтере
	 */
	toggleCounter({ toggle, quantity = 1 }) {
		const baskets = this.getBlockElements('[data-basket]');
		forEach(baskets, async (basket) => {
			basket.classList.toggle('product-control_in-basket', toggle);

			const counter = basket.querySelector('.counter');
			if (!counter) return;

			if (!counter?.Counter) {
				if (!toggle) return;
				window.Block.Counter.init(basket, true);
			}
			const ins = await counter?.AsyncCounter;
			ins.input.value = toggle ? quantity : 0;
			ins.changeHandler();
		});
	}

	/***
	 * Переключение работы к каунтера и кнопки в корзину
	 * @param toggle {Boolean} - вкл/выкл
	 * @param limit {Boolean} - если достигнут лимит
	 */
	toggleDisabledCounter({ toggle, limit }) {
		const cartButtons = this.getBlockElements('[data-control-cart]');
		const cartCounters = this.getBlockElements('.counter');

		forEach(cartCounters, async (counter) => {
			counter.classList.toggle('disabled', toggle);
			const ins = await counter?.AsyncCounter;
			if (toggle || limit) ins?.plus.setAttribute('disabled', '');
			else ins?.plus.removeAttribute('disabled');
		});

		forEach(cartButtons, (btn) => {
			if (toggle) btn.setAttribute('disabled', '');
			else btn.removeAttribute('disabled');
		});
	}

	/***
	 * Установка нового оффера
	 * @param id{String|Number} - id оффера
	 */
	setOffer(id) {
		this.offerHash = this.getCurrentHash();
		this.currentOffer = find(this.offers, { id });
		this.canBuy = includes(['IN_STOCK', 'WAREHOUSE'], get(this.currentOffer, 'status.code'));
		this.comingSoon = get(this.currentOffer, 'status.code') === 'COMING_SOON';

		if (!isEmpty(this.currentOffer)) this.render();
	}

	/***
	 * Получение данных из переданных параметров
	 * @param options{Object} - параметры
	 */
	getData(options) {
		this.data = get(options, 'data', {});
		this.offers = get(this.data, 'offers', []);
		this.languages = get(this.data, 'languages', []);
		this.colors = get(this.data, 'colors', []);
		this.currentOffer = find(this.offers, { id: get(this.data, 'currentOfferId', null) });
		/**
		 * @type {Array<'favorite' | 'compare' | 'share'>}
		 */
		this.actionList = get(options, 'actionList', ['favorite', 'compare']);
		this.saleColors = reduce(this.offers, (res, offer) => {
			if (get(offer, 'hasSale', false)) set(res, get(offer, 'color'), true);
			return res;
		}, {});
    this.isSearchCard = this.block.hasAttribute('data-search-card');
	}

	getSku() {
		const currentLang = find(this.languages, { id: get(this.currentOffer, 'lang', null) });
		const currentColor = find(this.colors, { id: get(this.currentOffer, 'color', null) });
		const colorsIdForCurrentLang = get(currentLang, 'colors', []);

		const allValidColors = !isEmpty(colorsIdForCurrentLang) ? map(this.colors, (color) => {
			return merge(color, { lock: !includes(colorsIdForCurrentLang, color.id) });
		}) : this.colors;

		return [
			{ current: currentLang, values: this.languages, key: 'lang' },
			{ current: currentColor, values: allValidColors, key: 'color' }
		];
	}

	/***
	 * Получение элементов текущего блока
	 * @param selector{String} - селектор поиска
	 * @return {HTMLElement[]} - массив элементов
	 */
	getBlockElements(selector) {
		return compact(map(toArray(this.block.querySelectorAll(selector)), (el) => {
			if (el.closest(this.controlSelector) === this.block) return el;
		}));
	}

	/***
	 * Получение всех связанных карт с этим оффером
	 * @return {HTMLElement[]} - массив карточек
	 */
	getOffersCard() {
		return map(toArray(document.querySelectorAll(`[data-offer-id="${get(this.currentOffer, 'id', '')}"]`)), (el) => {
			if (el !== this.block) return el;
		});
	}

	/***
	 * Получение хеша товара
	 * @return {string} - хеш строка
	 */
	getCurrentHash() {
		return get(this.currentOffer, 'lang', '') + get(this.currentOffer, 'color', '');
	}

	/***
	 * Обновить управление карточкой
	 */
	static updateControl() {
		forEach(document.querySelectorAll('.product-control_inited'), (elem) => {
			const control = get(elem, 'ProductControl', null);
			control && control.renderControl();
		});
	}

  destroy() {
    if (this.block) {
      this.block.classList.remove('product-control_inited');
      delete this.block.ProductControl;
    }
  }
}

// Экспорт;
export default ProductControl;
window.Block.ProductControl = ProductControl;
